import React, { useState, useEffect, useCallback } from 'react';
import { getRequestNoParams } from './Services/HttpClient';
import './VedaIndexBrowser.css';

const VedaIndexBrowser = () => {
  const [view, setView] = useState('shakhas');
  const [shakhas, setShakhas] = useState([]);
  const [stats, setStats] = useState(null);
  const [kandas, setKandas] = useState([]);
  const [prapathakas, setPrapathakas] = useState([]);
  const [anuvakas, setAnuvakas] = useState([]);
  const [mantras, setMantras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  // Navigation context
  const [selectedShakha, setSelectedShakha] = useState(null);
  const [selectedKanda, setSelectedKanda] = useState(null);
  const [selectedPrapathaka, setSelectedPrapathaka] = useState(null);
  const [selectedAnuvaka, setSelectedAnuvaka] = useState(null);

  // Level names from shakha
  const [levelNames, setLevelNames] = useState({
    level1: 'Kanda', level2: 'Prapathaka', level3: 'Anuvaka', level4: 'Mantra'
  });

  const loadShakhas = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [shakhaResp, statsResp] = await Promise.all([
        getRequestNoParams('api/veda-index/'),
        getRequestNoParams('api/veda-index/stats')
      ]);
      setShakhas(shakhaResp.data || []);
      setStats(statsResp);
    } catch (err) {
      setError('Failed to load Veda Index: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadShakhas();
  }, [loadShakhas]);

  const navigateToShakha = async (shakha) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await getRequestNoParams(`api/veda-index/${shakha.id}`);
      setSelectedShakha(resp.shakha);
      setKandas(resp.kandas || []);
      setLevelNames({
        level1: shakha.level1_name || 'Kanda',
        level2: shakha.level2_name || 'Prapathaka',
        level3: shakha.level3_name || 'Anuvaka',
        level4: shakha.level4_name || 'Mantra',
      });
      setView('kandas');
    } catch (err) {
      setError('Failed to load Shakha: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const navigateToKanda = async (kanda) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await getRequestNoParams(`api/veda-index/${selectedShakha.id}/${kanda.number}`);
      setSelectedKanda(kanda);
      setPrapathakas(resp.prapathakas || []);
      setView('prapathakas');
    } catch (err) {
      setError('Failed to load ' + levelNames.level1 + ': ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const navigateToPrapathaka = async (prapathaka) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await getRequestNoParams(
        `api/veda-index/${selectedShakha.id}/${selectedKanda.number}/${prapathaka.number}`
      );
      setSelectedPrapathaka(prapathaka);
      setAnuvakas(resp.anuvakas || []);
      setView('anuvakas');
    } catch (err) {
      setError('Failed to load ' + levelNames.level2 + ': ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const navigateToAnuvaka = async (anuvaka) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await getRequestNoParams(
        `api/veda-index/${selectedShakha.id}/${selectedKanda.number}/${selectedPrapathaka.number}/${anuvaka.number}`
      );
      setSelectedAnuvaka(anuvaka);
      setMantras(resp.mantras || []);
      setView('mantras');
    } catch (err) {
      setError('Failed to load ' + levelNames.level3 + ': ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const navigateTo = (level) => {
    setSearchResults(null);
    setSearchQuery('');
    if (level === 'shakhas') {
      setView('shakhas');
      setSelectedShakha(null);
      setSelectedKanda(null);
      setSelectedPrapathaka(null);
      setSelectedAnuvaka(null);
    } else if (level === 'kandas') {
      setView('kandas');
      setSelectedKanda(null);
      setSelectedPrapathaka(null);
      setSelectedAnuvaka(null);
    } else if (level === 'prapathakas') {
      setView('prapathakas');
      setSelectedPrapathaka(null);
      setSelectedAnuvaka(null);
    } else if (level === 'anuvakas') {
      setView('anuvakas');
      setSelectedAnuvaka(null);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    setSearchLoading(true);
    setError(null);
    try {
      const resp = await getRequestNoParams(`api/veda-index/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchResults(resp);
    } catch (err) {
      setError('Search failed: ' + err.message);
    } finally {
      setSearchLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchResults(null);
    setSearchQuery('');
  };

  const renderBreadcrumb = () => (
    <nav className="vib-breadcrumb">
      <span className="vib-breadcrumb-item" onClick={() => navigateTo('shakhas')}>
        Home
      </span>
      {selectedShakha && (
        <>
          <span className="vib-breadcrumb-sep">&gt;</span>
          <span
            className={`vib-breadcrumb-item ${view === 'kandas' ? 'vib-breadcrumb-active' : ''}`}
            onClick={() => navigateTo('kandas')}
          >
            {selectedShakha.name}
          </span>
        </>
      )}
      {selectedKanda && (
        <>
          <span className="vib-breadcrumb-sep">&gt;</span>
          <span
            className={`vib-breadcrumb-item ${view === 'prapathakas' ? 'vib-breadcrumb-active' : ''}`}
            onClick={() => navigateTo('prapathakas')}
          >
            {levelNames.level1} {selectedKanda.number}
            {selectedKanda.name_devanagari ? ` ${selectedKanda.name_devanagari}` : ''}
          </span>
        </>
      )}
      {selectedPrapathaka && (
        <>
          <span className="vib-breadcrumb-sep">&gt;</span>
          <span
            className={`vib-breadcrumb-item ${view === 'anuvakas' ? 'vib-breadcrumb-active' : ''}`}
            onClick={() => navigateTo('anuvakas')}
          >
            {levelNames.level2} {selectedPrapathaka.number}
            {selectedPrapathaka.name ? ` — ${selectedPrapathaka.name}` : ''}
          </span>
        </>
      )}
      {selectedAnuvaka && (
        <>
          <span className="vib-breadcrumb-sep">&gt;</span>
          <span className="vib-breadcrumb-item vib-breadcrumb-active">
            {levelNames.level3} {selectedAnuvaka.number}
          </span>
        </>
      )}
    </nav>
  );

  const renderStats = () => {
    if (!stats) return null;
    return (
      <div className="vib-stats">
        <div className="vib-stat">
          <div className="vib-stat-value">{stats.total_shakhas}</div>
          <div className="vib-stat-label">Shakhas</div>
        </div>
        <div className="vib-stat">
          <div className="vib-stat-value">{stats.total_kandas}</div>
          <div className="vib-stat-label">Kandas</div>
        </div>
        <div className="vib-stat">
          <div className="vib-stat-value">{stats.total_prapathakas}</div>
          <div className="vib-stat-label">Prapathakas</div>
        </div>
        <div className="vib-stat">
          <div className="vib-stat-value">{stats.total_anuvakas}</div>
          <div className="vib-stat-label">Anuvakas</div>
        </div>
        <div className="vib-stat">
          <div className="vib-stat-value">{stats.total_mantras}</div>
          <div className="vib-stat-label">Mantras</div>
        </div>
      </div>
    );
  };

  const renderSearch = () => (
    <form className="vib-search" onSubmit={handleSearch}>
      <input
        type="text"
        className="vib-search-input"
        placeholder="Search mantras, subjects..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />
      <button type="submit" className="vib-search-btn" disabled={searchLoading}>
        {searchLoading ? 'Searching...' : 'Search'}
      </button>
      {searchResults && (
        <button type="button" className="vib-search-clear" onClick={clearSearch}>
          Clear
        </button>
      )}
    </form>
  );

  const renderSearchResults = () => {
    if (!searchResults) return null;
    return (
      <div className="vib-search-results">
        <h3 className="vib-section-title">
          Search Results for "{searchResults.query}" ({searchResults.count} found)
        </h3>
        {searchResults.results.length === 0 ? (
          <p className="vib-empty">No results found.</p>
        ) : (
          <div className="vib-grid">
            {searchResults.results.map((r, i) => (
              <div key={i} className="vib-card vib-card-search">
                <div className="vib-card-badge">{r.type}</div>
                <div className="vib-card-name">{r.name || r.index || r.id}</div>
                {r.subject && <div className="vib-card-subject">{r.subject}</div>}
                <div className="vib-card-meta">
                  {r.shakha}
                  {r.kanda != null && ` / ${levelNames.level1} ${r.kanda}`}
                  {r.prapathaka != null && ` / ${levelNames.level2} ${r.prapathaka}`}
                  {r.anuvaka != null && ` / ${levelNames.level3} ${r.anuvaka}`}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderShakhas = () => (
    <>
      <h3 className="vib-section-title">Veda Shakhas</h3>
      <div className="vib-grid">
        {shakhas.map((s) => (
          <div key={s.id} className="vib-card vib-card-shakha" onClick={() => navigateToShakha(s)}>
            <div className="vib-card-name">
              {s.name_devanagari && <span className="vib-card-devanagari">{s.name_devanagari}</span>}
              {s.name}
            </div>
            <div className="vib-card-veda">{s.veda}</div>
            {s.description && <div className="vib-card-desc">{s.description}</div>}
            <div className="vib-card-counts">
              <span>{s.total_kandas} {s.level1_name}s</span>
              <span>{s.total_prapathakas} {s.level2_name}s</span>
              <span>{s.total_anuvakas} {s.level3_name}s</span>
              <span>{s.total_mantras} {s.level4_name}s</span>
            </div>
          </div>
        ))}
      </div>
    </>
  );

  const renderKandas = () => (
    <>
      <h3 className="vib-section-title">
        {selectedShakha.name} — {levelNames.level1}s
      </h3>
      <div className="vib-grid">
        {kandas.map((k) => (
          <div key={k.id} className="vib-card" onClick={() => navigateToKanda(k)}>
            <div className="vib-card-name">
              {levelNames.level1} {k.number}
              {k.name_devanagari && <span className="vib-card-devanagari-inline"> {k.name_devanagari}</span>}
            </div>
            <div className="vib-card-counts">
              <span>{k.prapathaka_count} {levelNames.level2}s</span>
              <span>{k.anuvaka_count} {levelNames.level3}s</span>
              <span>{k.mantra_count} {levelNames.level4}s</span>
            </div>
          </div>
        ))}
      </div>
    </>
  );

  const renderPrapathakas = () => (
    <>
      <h3 className="vib-section-title">
        {levelNames.level1} {selectedKanda.number} — {levelNames.level2}s
      </h3>
      <div className="vib-grid">
        {prapathakas.map((p) => (
          <div key={p.id} className="vib-card" onClick={() => navigateToPrapathaka(p)}>
            <div className="vib-card-name">
              {levelNames.level2} {p.number}
              {p.name && <span className="vib-card-subtitle"> — {p.name}</span>}
            </div>
            {p.subject && <div className="vib-card-subject">{p.subject}</div>}
            <div className="vib-card-counts">
              <span>{p.anuvaka_count} {levelNames.level3}s</span>
              <span>{p.mantra_count} {levelNames.level4}s</span>
            </div>
          </div>
        ))}
      </div>
    </>
  );

  const renderAnuvakas = () => (
    <>
      <h3 className="vib-section-title">
        {levelNames.level2} {selectedPrapathaka.number} — {levelNames.level3}s
      </h3>
      <div className="vib-grid">
        {anuvakas.map((a) => (
          <div key={a.id} className="vib-card" onClick={() => navigateToAnuvaka(a)}>
            <div className="vib-card-name">
              {a.index && <span className="vib-card-index">{a.index} </span>}
              {levelNames.level3} {a.number}
              {a.name && <span className="vib-card-subtitle"> — {a.name}</span>}
            </div>
            {a.subject && <div className="vib-card-subject">{a.subject}</div>}
            {a.mantra_count != null && (
              <div className="vib-card-counts">
                <span>{a.mantra_count} {levelNames.level4}s</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );

  const renderMantras = () => (
    <>
      <h3 className="vib-section-title">
        {levelNames.level3} {selectedAnuvaka.number} — {levelNames.level4}s
      </h3>
      <div className="vib-mantra-list">
        {mantras.length === 0 ? (
          <p className="vib-empty">No mantras found.</p>
        ) : (
          mantras.map((m, i) => (
            <div key={m.id} className="vib-mantra">
              <div className="vib-mantra-index">{m.index || `${i + 1}`}</div>
              <div className="vib-mantra-body">
                {m.starting_text && (
                  <div className="vib-mantra-text">{m.starting_text}</div>
                )}
                {m.subject && <div className="vib-mantra-subject">{m.subject}</div>}
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );

  const renderContent = () => {
    if (searchResults) return renderSearchResults();
    switch (view) {
      case 'shakhas': return renderShakhas();
      case 'kandas': return renderKandas();
      case 'prapathakas': return renderPrapathakas();
      case 'anuvakas': return renderAnuvakas();
      case 'mantras': return renderMantras();
      default: return renderShakhas();
    }
  };

  return (
    <div className="vib-container">
      <h2 className="vib-title">वेद सूची — Veda Index</h2>
      {renderBreadcrumb()}
      {view === 'shakhas' && renderStats()}
      {renderSearch()}

      {error && <div className="vib-error">{error}</div>}

      {loading ? (
        <div className="vib-loading">
          <div className="vib-spinner"></div>
          <span>Loading...</span>
        </div>
      ) : (
        renderContent()
      )}
    </div>
  );
};

export default VedaIndexBrowser;
